// /customers/feedback — Customer Feedback (PAGES.md §3.82)
//
// Rating aggregate + distribution + trend + filterable feedback stream.
// Reuses ReportPageShell + ReportFilterBar for consistency with the reports.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { Alert, Badge, BarChart, EmptyState, SalesWaveChart, Select } from '@bbpos/ui';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  KpiIcons,
  useReportFilter,
  DEFAULT_REPORT_PRESET,
  downloadCsv,
  shareReportLink,
} from '@/features/reports';
import { useFeedbackList, useFeedbackSummary, formatDateTime } from '@/features/customers';
import type { CustomerFeedbackEntry } from '@/features/customers';

const RATING_OPTIONS = [
  { value: '', label: 'All Ratings' },
  { value: '5', label: '★★★★★ (5)' },
  { value: '4', label: '★★★★☆ (4)' },
  { value: '3', label: '★★★☆☆ (3)' },
  { value: '2', label: '★★☆☆☆ (2)' },
  { value: '1', label: '★☆☆☆☆ (1)' },
];

type Row = CustomerFeedbackEntry;

function StarBadge({ rating }: { rating: 1 | 2 | 3 | 4 | 5 }) {
  const variant = rating >= 4 ? 'success' : rating === 3 ? 'warning' : 'danger';
  const stars = '★'.repeat(rating) + '☆'.repeat(5 - rating);
  return (
    <Badge variant={variant} size="sm">
      <span className="tabular-nums">{stars}</span>
    </Badge>
  );
}

const columns: ColumnDef<Row>[] = [
  {
    key: 'date',
    header: 'Received',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">{formatDateTime(row.date)}</span>,
  },
  {
    key: 'customer_name',
    header: 'Customer',
    render: (_v, row) => (
      <div>
        {row.customer_id ? (
          <Link to={`/customers/${row.customer_id}`} className="text-sm font-semibold text-brand-600 dark:text-brand-400 hover:underline">{row.customer_name ?? 'Anonymous'}</Link>
        ) : (
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.customer_name ?? 'Anonymous'}</p>
        )}
        {row.staff_name ? <p className="text-xs text-neutral-500 dark:text-neutral-400">served by {row.staff_name}</p> : null}
      </div>
    ),
  },
  {
    key: 'rating',
    header: 'Rating',
    render: (_v, row) => <StarBadge rating={row.rating} />,
  },
  {
    key: 'comment',
    header: 'Comment',
    render: (_v, row) => (
      <p className="text-sm text-neutral-700 dark:text-neutral-300 max-w-md truncate">{row.comment ?? '—'}</p>
    ),
  },
  {
    key: 'bill_number',
    header: 'Bill',
    render: (_v, row) =>
      row.bill_id ? (
        <Link to={`/bills/${row.bill_id}`} className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-mono tabular-nums">{row.bill_number ?? '—'}</Link>
      ) : (
        <span className="text-xs text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">{row.bill_number ?? '—'}</span>
      ),
  },
  {
    key: 'location_name',
    header: 'Location',
    render: (_v, row) => <span className="text-xs text-neutral-500 dark:text-neutral-400">{row.location_name ?? '—'}</span>,
  },
];

export function CustomerFeedbackPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [rating, setRating] = useState<string>('');
  const [page, setPage] = useState(1);

  const filter = useReportFilter(timeRange, locationId);

  const filters = {
    rating: rating ? Number(rating) : undefined,
    location_id: locationId,
    from: filter.range.from,
    to: filter.range.to,
  };
  const summaryQ = useFeedbackSummary(filters);
  const listQ = useFeedbackList({ page, ...filters });

  const rows = useMemo<Row[]>(() => listQ.data?.data ?? [], [listQ.data]);
  const summary = summaryQ.data;

  const distributionData = (summary?.rating_distribution ?? [])
    .sort((a, b) => b.rating - a.rating)
    .map((d) => ({
      label: `${d.rating}★`,
      value: d.count,
      color: d.rating >= 4 ? '#10b981' : d.rating === 3 ? '#f59e0b' : '#ef4444',
    }));

  const trendData = (summary?.trend ?? []).map((t) => ({
    label: t.date.slice(5),
    today: t.avg_rating,
    yesterday: 0,
  }));

  function handleExport(fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') {
    if (fmt === 'csv') {
      downloadCsv(
        `customer-feedback-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Date', get: (r: Row) => r.date },
          { header: 'Customer', get: (r: Row) => r.customer_name ?? '' },
          { header: 'Rating', get: (r: Row) => r.rating },
          { header: 'Comment', get: (r: Row) => r.comment ?? '' },
          { header: 'Bill', get: (r: Row) => r.bill_number ?? '' },
          { header: 'Staff', get: (r: Row) => r.staff_name ?? '' },
          { header: 'Location', get: (r: Row) => r.location_name ?? '' },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(
        fmt,
        'Orio360 Customer Feedback',
        `Avg ${summary?.avg_rating.toFixed(1) ?? '—'}★ • ${summary?.total_reviews ?? 0} reviews • ${summary?.negative_count ?? 0} negative`,
      );
    }
  }

  const isLoading = summaryQ.isLoading || listQ.isLoading;
  const error = summaryQ.error || listQ.error;

  return (
    <ReportPageShell
      title="Customer Feedback"
      subtitle="Star ratings and comments collected via digital receipt links."
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Customers', href: '/customers' },
        { label: 'Feedback' },
      ]}
      accent="violet-pink"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={
        <ReportFilterBar
          timeRange={timeRange}
          onTimeRangeChange={(v) => {
            setTimeRange(v);
            setPage(1);
          }}
          locationId={locationId}
          onLocationChange={(id) => {
            setLocationId(id);
            setPage(1);
          }}
          extraFilters={
            <div className="w-full sm:w-44">
              <Select
                options={RATING_OPTIONS}
                value={rating}
                onChange={(v) => {
                  setRating(typeof v === 'string' ? v : '');
                  setPage(1);
                }}
                placeholder="All Ratings"
              />
            </div>
          }
        />
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : summary && summary.total_reviews === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No feedback yet"
            description="Feedback will appear here when customers rate their experience via receipt links."
            icon={
              <svg className="h-20 w-20 text-warning-300 dark:text-warning-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            }
          />
        </div>
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'avg', title: 'Avg Rating', value: `${(summary?.avg_rating ?? 0).toFixed(1)}★`, tone: (summary?.avg_rating ?? 0) >= 4 ? 'success' : (summary?.avg_rating ?? 0) >= 3 ? 'warning' : 'danger', icon: KpiIcons.star },
              { id: 'total', title: 'Total Reviews', value: (summary?.total_reviews ?? 0).toLocaleString('en-IN'), tone: 'indigo', icon: KpiIcons.users },
              { id: 'positive', title: 'Positive %', value: `${(summary?.positive_pct ?? 0).toFixed(1)}%`, tone: 'success', icon: KpiIcons.trendUp },
              { id: 'negative', title: 'Negative Alerts', value: (summary?.negative_count ?? 0).toLocaleString('en-IN'), tone: 'danger', icon: KpiIcons.trendDown },
            ]}
          />

          {summary && summary.negative_count > 0 ? (
            <div className="mb-6">
              <Alert
                variant="danger"
                title={`${summary.negative_count} negative review${summary.negative_count === 1 ? '' : 's'} need attention`}
                description="Filter by 1★ or 2★ to respond directly — quick recovery turns detractors into loyalists."
              />
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            {distributionData.length > 0 ? (
              <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-5 animate-slide-up">
                <BarChart data={distributionData} title="Rating Distribution" />
              </div>
            ) : null}
            {trendData.length > 0 ? (
              <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-5 animate-slide-up">
                <SalesWaveChart data={trendData} valueFormat="number" title="Average Rating Trend" />
              </div>
            ) : null}
          </div>

          <ReportTable
            title="Recent Feedback"
            columns={columns}
            data={rows}
            loading={listQ.isFetching}
            totalCount={listQ.data?.meta.total}
            currentPage={page}
            onPageChange={setPage}
            emptyTitle="No feedback yet"
            emptyDescription="Feedback will appear here when customers rate their experience via receipt links."
          />
        </>
      )}
    </ReportPageShell>
  );
}
